import { useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Film, X } from "lucide-react";
import { TkticSelect } from "@/components/ui/TkticSelect";
import type { Match } from "@/lib/types";
import type { UploadSlot } from "@/features/coach/components/useUploadQueue";
import { defaultSlotForMatch, SLOT_LABELS, slotsForMatch } from "../matchSlots";

/** Captures a recording without starting a second, modal-owned upload. */
export function MatchUploadQueueDialog({
  match,
  initialSlot,
  onAdd,
  onClose,
  testIdPrefix = "admin-add-to-queue",
}: {
  match: Match;
  initialSlot?: UploadSlot;
  onAdd: (slot: UploadSlot, file: File) => void;
  onClose: () => void;
  testIdPrefix?: string;
}) {
  const slotOptions = slotsForMatch(match);
  const [slot, setSlot] = useState<UploadSlot>(() =>
    initialSlot && slotOptions.includes(initialSlot)
      ? initialSlot
      : defaultSlotForMatch(match),
  );
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [popoverContainer, setPopoverContainer] =
    useState<HTMLFormElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const returnFocus = useRef(document.activeElement);
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="admin-match-upload-overlay">
          <Dialog.Content
            asChild
            aria-label="Add match video to upload queue"
            onOpenAutoFocus={(event) => {
              event.preventDefault();
              fileInputRef.current?.focus();
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              const previous = returnFocus.current;
              const fallback = document.querySelector<HTMLButtonElement>(
                `[data-match-actions="${CSS.escape(match.id)}"] button`,
              );
              if (
                previous instanceof HTMLElement &&
                previous.isConnected &&
                previous !== document.body
              )
                previous.focus();
              else fallback?.focus();
            }}
            onEscapeKeyDown={(event) => {
              // Radix listens in capture before the select's React key handler.
              // Let the first Escape close its popup without dismissing the form.
              if (
                popoverContainer?.querySelector('[data-tktic-popover="select"]')
              )
                event.preventDefault();
            }}
          >
            <form
              ref={setPopoverContainer}
              className="admin-match-upload-dialog"
              data-testid={`${testIdPrefix}-modal`}
              onSubmit={(event) => {
                event.preventDefault();
                if (!file) {
                  setError("Pick a video file (.mp4 or .mkv) first.");
                  return;
                }
                if (!/\.(mp4|mkv)$/i.test(file.name) || file.size === 0) {
                  setError("Choose a non-empty MP4 or MKV recording.");
                  return;
                }
                onAdd(slot, file);
              }}
            >
              <header>
                <Dialog.Title>
                  {match.home_team || "—"} vs {match.away_team || "—"} ·{" "}
                  {SLOT_LABELS[slot]}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    data-testid={`${testIdPrefix}-close`}
                    aria-label="Close"
                  >
                    <X size={20} aria-hidden="true" />
                  </button>
                </Dialog.Close>
              </header>
              <div className="admin-match-upload-body">
                <div className="admin-match-upload-context">
                  <div className="admin-match-upload-field">
                    <span>Match</span>
                    <div className="admin-match-upload-locked">
                      <Film size={17} aria-hidden="true" />
                      <strong>
                        {match.home_team || "—"} vs {match.away_team || "—"}
                      </strong>
                    </div>
                  </div>
                  <label className="admin-match-upload-field">
                    <span>Video segment</span>
                    <TkticSelect
                      portalContainer={popoverContainer}
                      value={slot}
                      onChange={(next) => setSlot(next as UploadSlot)}
                      ariaLabel="Video segment"
                      data-testid={`${testIdPrefix}-slot`}
                      options={slotOptions.map((value) => ({
                        value,
                        label: SLOT_LABELS[value],
                      }))}
                    />
                  </label>
                </div>
                <p className="admin-match-upload-help">
                  {match.format === "full"
                    ? "Use one file containing the whole match, including extra time or penalties if recorded."
                    : "Add each recording to its matching slot. Extra time and penalty shootout are optional."}
                </p>
                <label className="admin-match-upload-file">
                  <span>Recording file</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp4,.mkv,video/mp4,video/x-matroska"
                    data-testid={`${testIdPrefix}-file`}
                    onChange={(event) => {
                      setFile(event.target.files?.[0] ?? null);
                      setError(null);
                    }}
                  />
                  {file ? (
                    <small>
                      {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
                    </small>
                  ) : null}
                </label>
                <Dialog.Description className="admin-match-upload-help">
                  Add recordings from several matches or segments. They upload
                  one at a time while you use this workspace. After a browser
                  refresh, reselect files to resume.
                </Dialog.Description>
                {error ? (
                  <p
                    role="alert"
                    className="admin-match-upload-error"
                    data-testid={`${testIdPrefix}-error`}
                  >
                    {error}
                  </p>
                ) : null}
              </div>
              <footer>
                <Dialog.Close asChild>
                  <button type="button" className="admin-match-upload-cancel">
                    Cancel
                  </button>
                </Dialog.Close>
                <button
                  type="submit"
                  disabled={!file}
                  className="admin-match-upload-submit"
                  data-testid={`${testIdPrefix}-submit`}
                >
                  Add to queue
                </button>
              </footer>
            </form>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
